import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Settings, MessageSquare, CheckCircle2, XCircle, Send, Bot } from "lucide-react";

const AI_LABELS = { "gpt-5.4": "GPT-5.4", "gpt-5.4-mini": "GPT-5.4 Mini", "gpt-5.5": "GPT-5.5", "gpt-5.6-sol": "GPT-5.6 Sol", "claude-sonnet-5": "Claude Sonnet 5", "claude-opus-4-8": "Claude Opus 4.8", "gemini-3.1-pro": "Gemini 3.1 Pro", "gemini-3.5-flash": "Gemini 3.5 Flash" };
const AI_COST_KEY = { "claude-opus-4-8": "ai_cost_high", "gpt-5.6-sol": "ai_cost_high", "gemini-3.1-pro": "ai_cost_high", "gpt-5.4-mini": "ai_cost_low", "gemini-3.5-flash": "ai_cost_low" };

export default function Parametres() {
  const { t } = useI18n();
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({
    twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "",
    twilio_whatsapp_from: "", alert_sms_to: "",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [aiUsage, setAiUsage] = useState([]);

  async function load() {
    try {
      const r = await api.get("/settings/integrations");
      setStatus(r.data.twilio);
    } catch { /* ignore */ }
    try {
      const u = await api.get("/ai/usage");
      setAiUsage(u.data);
    } catch { /* ignore */ }
  }
  useEffect(() => { load(); }, []);

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v.trim()));
      if (!Object.keys(payload).length) { toast.error(t("error")); setSaving(false); return; }
      const r = await api.put("/settings/integrations", payload);
      setStatus(r.data.twilio);
      setForm({ twilio_account_sid: "", twilio_auth_token: "", twilio_phone_number: "", twilio_whatsapp_from: "", alert_sms_to: "" });
      toast.success(t("updated"));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setSaving(false);
  }

  async function testSms() {
    setTesting(true);
    try {
      const r = await api.post("/settings/integrations/twilio/test");
      toast.success(`SMS: ${r.data.sms ? "OK" : "échec"} · WhatsApp: ${r.data.whatsapp ? "OK" : "échec"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
    setTesting(false);
  }

  return (
    <div className="max-w-2xl space-y-6" data-testid="settings-page">
      <div>
        <div className="label-eyebrow mb-2">{t("role_admin")}</div>
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight flex items-center gap-3">
          <Settings className="w-8 h-8 text-primary" />{t("settings")}
        </h1>
        <p className="text-muted-foreground mt-1">{t("settings_sub")}</p>
      </div>

      <Card className="border-border">
        <CardContent className="p-6 space-y-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <MessageSquare className="w-5 h-5" />
              </div>
              <div>
                <div className="font-semibold">Twilio — SMS & WhatsApp</div>
                <div className="text-xs text-muted-foreground">{t("twilio_sub")}</div>
              </div>
            </div>
            {status && (
              <Badge variant={status.configured ? "default" : "secondary"} data-testid="twilio-status">
                {status.configured
                  ? <span className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" />{t("twilio_active")}</span>
                  : <span className="flex items-center gap-1"><XCircle className="w-3.5 h-3.5" />{t("twilio_inactive")}</span>}
              </Badge>
            )}
          </div>

          {status?.configured && (
            <div className="rounded-lg bg-secondary/60 p-4 text-sm space-y-1" data-testid="twilio-current">
              <div>Account SID : <span className="font-mono">{status.account_sid_masked}</span></div>
              <div>Numéro : <span className="font-mono">{status.phone_from}</span></div>
              {status.whatsapp_from && <div>WhatsApp : <span className="font-mono">{status.whatsapp_from}</span></div>}
              <div>Alertes vers : <span className="font-mono">{status.alert_to_masked}</span></div>
            </div>
          )}

          <form onSubmit={save} className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="twilio-form">
            <div><Label>TWILIO_ACCOUNT_SID</Label><Input data-testid="twilio-sid" value={form.twilio_account_sid} onChange={(e) => setForm({ ...form, twilio_account_sid: e.target.value })} placeholder="ACxxxxxxxx" /></div>
            <div><Label>TWILIO_AUTH_TOKEN</Label><Input type="password" data-testid="twilio-token" value={form.twilio_auth_token} onChange={(e) => setForm({ ...form, twilio_auth_token: e.target.value })} placeholder="••••••" /></div>
            <div><Label>{t("twilio_phone")}</Label><Input data-testid="twilio-phone" value={form.twilio_phone_number} onChange={(e) => setForm({ ...form, twilio_phone_number: e.target.value })} placeholder="+1415…" /></div>
            <div><Label>WhatsApp From</Label><Input data-testid="twilio-wa" value={form.twilio_whatsapp_from} onChange={(e) => setForm({ ...form, twilio_whatsapp_from: e.target.value })} placeholder="whatsapp:+14155238886" /></div>
            <div className="md:col-span-2"><Label>{t("alert_phone")}</Label><Input data-testid="twilio-to" value={form.alert_sms_to} onChange={(e) => setForm({ ...form, alert_sms_to: e.target.value })} placeholder="+237 6XX XX XX XX" /></div>
            <div className="md:col-span-2 flex gap-2">
              <Button type="submit" disabled={saving} data-testid="twilio-save">{saving ? "…" : t("save")}</Button>
              <Button type="button" variant="outline" onClick={testSms} disabled={testing || !status?.configured} data-testid="twilio-test">
                <Send className="w-4 h-4 mr-1" />{testing ? "…" : t("twilio_test")}
              </Button>
            </div>
          </form>

          <p className="text-xs text-muted-foreground">{t("twilio_hint")}</p>
        </CardContent>
      </Card>

      <Card className="border-border" data-testid="ai-usage-card">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
              <Bot className="w-5 h-5" />
            </div>
            <div>
              <div className="font-semibold">{t("ai_usage")}</div>
              <div className="text-xs text-muted-foreground">{t("ai_usage_sub")}</div>
            </div>
          </div>
          <div className="space-y-2">
            {aiUsage.map((u) => (
              <div key={u.model} className="flex items-center justify-between rounded-lg bg-secondary/60 px-4 py-2.5 text-sm" data-testid={`ai-usage-${u.model}`}>
                <div className="font-medium">{AI_LABELS[u.model] || u.model}</div>
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span>{u.requests} {t("requests").toLowerCase()}</span>
                  <span className="font-mono">{u.total_tokens.toLocaleString()} tokens</span>
                  <Badge variant={AI_COST_KEY[u.model] ? "destructive" : "secondary"}>
                    {t(AI_COST_KEY[u.model] || "ai_cost_standard")}
                  </Badge>
                </div>
              </div>
            ))}
            {aiUsage.length === 0 && (
              <div className="text-sm text-muted-foreground text-center py-3">{t("no_data")}</div>
            )}
          </div>
          <p className="text-xs text-muted-foreground">{t("ai_balance_hint")}</p>
        </CardContent>
      </Card>
    </div>
  );
}
